import {
  AloftPrice,
  CraftsmanCostProps,
  Fireplace,
  ORDER_FEATURES,
  getCraftsmanCost,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectImprovementEntityIds,
  selectImprovementsEntities,
} from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectPropertyIdentificationEntities } from '../propertyDataSelectors/propertyIdentificationSelectors.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { selectCostProps } from './selectCostProps.ts';

const calculateFireplaceCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) => {
  const improvementsForTransaction = improvements[id];
  const identification = propertyIdentification[id];

  if (!improvementsForTransaction || !identification) return undefined;

  const { fireplace, craftsmanQuality } = improvementsForTransaction;
  const zipCode = identification.address.PostalCode;

  if (
    !fireplace ||
    isUndefined(craftsmanQuality) ||
    isUndefined(zipCode) ||
    !ORDER_FEATURES.fireplace.hasFeature(improvementsForTransaction)
  )
    return undefined;

  const calculator = getCraftsmanCost(costProps);
  return calculator.getFireplaceCost({ ...(fireplace as Required<Fireplace>), zipCode });
};

const getTotalOrBreakdownFireplaceCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
  type: 'totalCost' | 'zipOnly',
) => {
  const costOutput = calculateFireplaceCost(costProps, improvements, propertyIdentification, id);

  if (!costOutput) return AloftPrice.zero;
  return type === 'totalCost' ? costOutput.totalCost : costOutput.multiplierBreakdown[type];
};

const getTotalFireplaceCost = (
  costProps: CraftsmanCostProps,
  improvements: ReturnType<typeof selectImprovementsEntities>,
  propertyIdentification: ReturnType<typeof selectPropertyIdentificationEntities>,
  id: string,
) =>
  getTotalOrBreakdownFireplaceCost(
    costProps,
    improvements,
    propertyIdentification,
    id,
    'totalCost',
  );

export const selectFireplaceCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  getTotalFireplaceCost,
);

export const selectZipOnlyFireplaceCost = createPriceSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    (state, id: string) => id,
  ],
  (costProps, improvements, propertyIdentification, id) =>
    getTotalOrBreakdownFireplaceCost(
      costProps,
      improvements,
      propertyIdentification,
      id,
      'zipOnly',
    ),
);

export const selectFireplaceCosts = createSelector(
  [
    selectCostProps,
    selectImprovementsEntities,
    selectPropertyIdentificationEntities,
    selectImprovementEntityIds,
  ],
  (costProps, improvements, propertyIdentification, improvementEntityIds) =>
    improvementEntityIds.reduce((acc: Record<string, AloftPrice>, propID) => {
      const cost = getTotalFireplaceCost(costProps, improvements, propertyIdentification, propID);
      if (cost) acc[propID] = cost;
      return acc;
    }, {}),
);

export const selectFireplaceCostDisplay = createSelector([selectFireplaceCost], (fireplaceCost) =>
  fireplaceCost?.toString(true),
);
